import { IGraph } from './IGraph';
import { INode } from './INode';
import { Node } from './Node';
import { AnswerMap } from '../AnswerMap';
import { FriendlyAnswer } from '../FriendlyAnswer';
import { StringMap } from '../i18n';

export class Graph {
  id: string;

  defaults: any;

  nodes: Node[];

  nodeMap: Map<string, Node> = new Map<string, Node>();

  constructor(sourceGraph: IGraph) {
    this.id = sourceGraph.id;
    this.defaults = sourceGraph.defaults;

    this.nodes = [];
    sourceGraph.nodes.forEach((sourceNode: INode) => {
      const newNode: Node = new Node(sourceNode);
      this.nodes.push(newNode);
      this.nodeMap.set(newNode.id, newNode);
    });
  }

  public isTerminalPath(answers: AnswerMap): boolean {
    // Iterate through the graph, and return true if the given answer set satisfies a complete
    // traversal.
    let node: Node | null | undefined = this.nodes[0];

    while (node != null) {
      const nextNodeId: string | null = node.getNextNode(answers);
      if (nextNodeId === null) {
        if ((answers[node.id] !== undefined || !node.evaluateCondition(answers)) && node.isTerminalNode(answers[node.id])) {
          // Reached the end
          return true;
        }
        // No answer for this node, so cannot proceed
        node = null;
      } else {
        node = this.getNode(nextNodeId);
      }
    }

    return false;
  }

  public getEntryNode(): Node {
    return this.nodes[0];
  }

  public getNode(nodeId: string): Node | undefined {
    return this.nodeMap.get(nodeId);
  }

  public getProgressPercentage(currentNodeId: string): number {
    // Iterate over the graph depth-first and count the number of nodes until the one we're at.
    let visitedNodes: number = 0;
    for (let i = 0; i < this.nodes.length; i++) {
      if (this.nodes[i].id === currentNodeId) {
        visitedNodes = i;
        break;
      }
    }


    return Math.round((visitedNodes / this.nodes.length) * 100);
  }

  public getSortedAnswerKeysAndRemoveHidden(answers: AnswerMap): string[] {
    // Get the keys of all answers in the answer set sorted by their position in the question graph
    const sortedKeys: string[] = [];

    this.nodes.forEach((node: Node) => {
      if (!node.hideFromSummary && answers[node.id]) {
        sortedKeys.push(node.id);
      }
    });
    return sortedKeys;
  }

  public buildFriendlyAnswers(answers: AnswerMap, fiSettings: AnswerMap, stringMap: StringMap): FriendlyAnswer[] {
    const nodeIds: string[] = Object.keys(answers);
    const friendlyAnswers: FriendlyAnswer[] = [];
    nodeIds.forEach((id: string) => {
      const questionNode: Node | undefined = this.getNode(id);
      if (questionNode) {
        friendlyAnswers.push(questionNode.toFriendlyAnswer(answers, fiSettings, stringMap));
      }
    });

    return friendlyAnswers;
  }

  public buildAnswersForGeneration(providedAnswers: AnswerMap): AnswerMap {
    const answers: AnswerMap = {};
    const keys: string[] = Object.keys(providedAnswers);
    keys.forEach((key: string) => {
      const node: Node | undefined = this.getNode(key);

      if (!node) {
        // Should never happen, but just in case...
        throw new Error(`Failed to find node with id ${key}`);
      }

      if (node.attribute) {
        answers[node.attribute] = providedAnswers[key];
      }
    });
    return answers;
  }
}

export default Graph;
